import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from '@/utils/lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SlideshowText from '@components/page/SlideshowText';
import { useAppContext } from '@/context/AppContext';
import { findPageByPath, followUpExists, getPagePath } from '@/utils/pagePaths';
import { trackImageOpened } from '@/services/analytics';
import { getParentId } from '@/utils/pagePaths';
import { SiteLogoMark } from '@components/shared/FilteredMark';
import { compactMeta, shareImageMeta, shareSiteTitle, resolveMediaShareCard } from '@/share/meta';
import { mediaLook } from '@/blocks/look';
import { previewLibrary } from '@/preview/hoverChrome';
import useChromeLayer from '@/hooks/useChromeLayer';
import { pageViewerLook, themeViewerLook, viewerLookVars } from '@/theme/viewer-look';
import { ViewerLookContext } from './components/MediaFrame';
import { phoneTextSlot, slideshowChromeFor, spreadRatioOf } from '@/slideshow/settings';
import { chromeCornerClass } from '@/slideshow/chrome';
import { slideshowHasText, slideshowSettingsOf } from '@/slideshow/text';

import { calculateUnifiedPageSize, updateRawImageSettings, withSpreadRatio } from '../spreadLayoutUtils';

import {
    FADE_DURATION,
    ENABLE_VIEWER_PAGE_FADES,
    toUrl,
    parseImageParams,
    prepareEditorialSlidesData,
    prepareDesktopSlidesData,
    prepareDesktopEditorialSlidesData,
    flattenImages,
} from './imageViewerUtils';

import {
    useSlideshowExperience,
    useSwipeToClose,
    useSwipeNavigation,
    useKeyboardNavigation,
    useImagePreload,
    useOnboarding,
    useFadeTransition,
} from './hooks';

import {
    OnboardingOverlay,
    ImageSlide,
    SpreadSlide,
    PrevLink,
    NextLink,
    CloseButton,
    EditorialLogo,
    TouchSwiper,
    ImageNotFound,
    ImageCounter,
} from './components';
import EditorialFilmstrip, { postEditorial } from '@components/page/EditorialFilmstrip';
import { useEditorFrame } from '@components/preview/editGates';

/** Tall layout: the spread's row of the align anchor (t, m, b) as a column justify. */
const TALL_JUSTIFY = { t: 'justify-start', m: 'justify-center', b: 'justify-end' };

/**
 * Spread Viewer Component
 * Used on slideshow (editorial) type pages. Displays spreads, text panel, and touch swiper with text as first slide.
 * - Desktop wide: text panel on left, image viewer on right
 * - Desktop tall: image viewer on top, text panel below
 * - Mobile: text as first slide in swiper
 * - Appearance preview (`editorMode`): filmstrip for reorder/select + Arrange
 *
 * @param {Object} props.match - React Router match object
 * @param {Object} props.history - React Router history object
 * @param {Object} props.location - React Router location object
 * @param {boolean} props.startAtTextSlide - Start at text slide (e.g. when opening from Slideshow on mobile)
 * @param {string} props.overrideSlug - Override the page slug (bypasses router match)
 * @param {string} props.overrideImageId - Override the image ID (bypasses router match)
 * @param {boolean} props.editorMode - Appearance preview: keep editing chrome
 */
function SpreadViewer({
    match: matchProp,
    history: historyProp,
    location: locationProp,
    startAtTextSlide,
    overrideSlug,
    overrideImageId,
    contentAppearDelayMs,
    editorMode: editorModeProp,
}) {
    const params = useParams();
    const locationFromRouter = useLocation();
    const navigate = useNavigate();
    const location = locationProp ?? locationFromRouter;
    const match = matchProp ?? { params };
    const history = historyProp ?? {
        push: (url, state) => navigate(url, state ? { state } : undefined),
        replace: (url) => navigate(url, { replace: true }),
    };
    // Slide URLs are router paths; inside a subsite the address bar also has
    // the /~slug prefix, so compare with the router instead.
    const routerPathRef = useRef(locationFromRouter.pathname);
    routerPathRef.current = locationFromRouter.pathname;

    const { content, urlBase, theme } = useAppContext();
    const chromeLayer = useChromeLayer();
    const experience = useSlideshowExperience();
    const screenShape = experience.shape;
    const isEditorial = true;
    // Filmstrip / arrange chrome is Appearance-iframe only. A logged-in admin
    // browsing the live site gets window.__ADMIN__ (inlineEditingEnabled) but
    // must not see Unpair / Arrange / Spread gap on the public slideshow.
    const inAppearanceFrame = useEditorFrame();
    const editorMode = Boolean(editorModeProp) || inAppearanceFrame;

    const parsed = parseImageParams(match, location);
    const imageId = overrideImageId ?? parsed.imageId;
    const slug = overrideSlug ?? parsed.slug;
    const page = slug;
    const pagesObj = content?.pages || {};

    const isTouchDevice = experience.touch;

    const [isHydrated, setIsHydrated] = useState(false);
    const [isEntered, setIsEntered] = useState(!ENABLE_VIEWER_PAGE_FADES);
    const [isClosing, setIsClosing] = useState(false);
    const [isContentReady, setIsContentReady] = useState(contentAppearDelayMs <= 0);
    const [mobileSlideIndex, setMobileSlideIndex] = useState(null);

    const swiperRef = useRef(null);
    const figureRef = useRef(null);
    const swipeWrapperRef = useRef(null);
    const editorialWrapperRef = useRef(null);
    const imageContentRef = useRef(null);
    const { fadeOut, fadeOpacity, navigateWithFade } = useFadeTransition(history);

    const closeWithFade = useCallback((url, shouldFocus = false, { dismissed = false } = {}) => {
        if (isClosing) return;
        setIsClosing(true);
        if (shouldFocus && imageId && typeof window !== 'undefined') {
            sessionStorage.setItem('focusImageId', imageId);
        }
        history.push(url, dismissed ? { viewerDismissed: true } : undefined);
    }, [history, isClosing, imageId]);

    let pageDataObj = null;
    if (page) {
        pageDataObj = findPageByPath(page, pagesObj);
        if (!pageDataObj) {
            pageDataObj = pagesObj[page] || null;
        }
    }

    // Close / logo leave the slideshow (parent page or home). Never use the
    // slideshow page URL itself — that immediately redirects to the first slide.
    let closeUrl = '/';
    if (pageDataObj) {
        const parentId = getParentId(pageDataObj);
        if (parentId && pagesObj[parentId]) {
            const parentPath = getPagePath(pagesObj[parentId], pagesObj);
            closeUrl = parentPath ? `/${parentPath}` : '/';
        }
    }

    const properties = content?.properties || {};
    const pageData = pageDataObj || {};
    const name = pageData.name || '';
    const spreadGap = pageData.gallery?.spreadGap || pageData.spreadGap || 0;
    const exists = !page || !!pageDataObj;
    const rawImages = page && pageDataObj ? (pageDataObj.gallery?.images || pageDataObj.images || []) : _.result(content, 'home.gallery.images', _.result(content, 'home.images', []));
    const flatImages = useMemo(() => flattenImages(rawImages), [rawImages]);
    // The home slideshow keeps its text on the home object.
    const textPage = page ? pageDataObj : content?.home;
    const textSettings = useMemo(() => slideshowSettingsOf(textPage), [textPage]);
    const hasText = useMemo(() => slideshowHasText(textPage), [textPage]);
    const textSlot = hasText ? phoneTextSlot(textSettings) : null;
    const hasEditorialTextSlide = Boolean(textSlot);
    const textFirst = textSlot === 'first';
    const textLast = textSlot === 'last';
    const desktopText = hasText ? textSettings.desktop.placement : 'none';
    const files = content?.files || {};
    const pagesForHtml = content?.pages || {};
    const index = imageId ? flatImages.indexOf(imageId) : -1;
    const pageAdvanceUrl = page ? _.result(content, ['pages', page, 'advanceUrl'], null) : null;
    const advanceUrl = followUpExists(pageAdvanceUrl, pagesObj) ? pageAdvanceUrl : null;
    const isFirst = index === 0;
    const isLast = index === flatImages.length - 1;
    const images = flatImages;

    const desktopSlidesData = useMemo(() => {
        return prepareDesktopSlidesData(rawImages, content?.files, page, pagesObj);
    }, [rawImages, content?.files, page, pagesObj]);

    // Story-wide unified page size: same calculation as admin (spreadLayoutUtils).
    // Spreads: one page per image (page AR = image AR). Singles: image spans two pages (page AR = image AR / 2).
    // Median over all gallery items minimises white space. All spreads use this same bounding box.
    // page.slideshow.spread can set the sheet's shape by hand and where it sits on screen.
    // While the editor drags the sheet's edges the shape follows here, until the saved one arrives.
    const [draftSpreadRatio, setDraftSpreadRatio] = useState(null);
    const savedSpreadRatio = spreadRatioOf(textPage?.slideshow);
    const spreadRatio = draftSpreadRatio ?? savedSpreadRatio;
    useEffect(() => setDraftSpreadRatio(null), [savedSpreadRatio]);
    const spreadAlign = textSettings.spread.align;
    const spreadFill = textSettings.spread.fill;
    const pageDimensions = useMemo(() => {
        const getDimensions = (item) => item.file?.dimensions || null;
        return withSpreadRatio(calculateUnifiedPageSize(desktopSlidesData, getDimensions), spreadRatio, spreadGap);
    }, [desktopSlidesData, spreadRatio, spreadGap]);

    const desktopSlideIndex = useMemo(() => {
        if (!imageId || !desktopSlidesData.length) return -1;
        return desktopSlidesData.findIndex((slide) => {
            if (slide.type === 'spread') {
                return slide.images.some((img) => img.id === imageId);
            }
            return slide.id === imageId;
        });
    }, [imageId, desktopSlidesData]);

    const currentSlideData = desktopSlideIndex >= 0 ? desktopSlidesData[desktopSlideIndex] : null;
    const isCurrentSpread = currentSlideData?.type === 'spread';

    // Normalize slide to spreadData so single images use the same spread container (same position)
    const toSpreadData = (slide) => {
        if (!slide) return null;
        if (slide.type === 'spread' && slide.images) return slide;
        if (slide.type === 'image' || (slide.file && slide.id)) {
            return { type: 'spread', images: [slide] };
        }
        return null;
    };

    // Counter values for ImageCounter (1-indexed) – spread range or single position
    const { counterCurrent, counterCurrentEnd, counterTotal } = useMemo(() => {
        const total = flatImages.length;
        if (isCurrentSpread && currentSlideData?.images?.length === 2) {
            const leftId = currentSlideData.images[0]?.id;
            const rightId = currentSlideData.images[1]?.id;
            const leftIndex = flatImages.indexOf(leftId);
            const rightIndex = flatImages.indexOf(rightId);
            if (leftIndex >= 0 && rightIndex >= 0) {
                return {
                    counterCurrent: Math.min(leftIndex, rightIndex) + 1,
                    counterCurrentEnd: Math.max(leftIndex, rightIndex) + 1,
                    counterTotal: total,
                };
            }
        }
        return {
            counterCurrent: index >= 0 ? index + 1 : 1,
            counterCurrentEnd: null,
            counterTotal: total,
        };
    }, [isCurrentSpread, currentSlideData, flatImages, index]);

    const isFirstDesktopSlide = desktopSlideIndex === 0;
    const isLastDesktopSlide = desktopSlideIndex === desktopSlidesData.length - 1;

    const prevDesktopSlide = desktopSlideIndex > 0 ? desktopSlidesData[desktopSlideIndex - 1] : null;
    const nextDesktopSlide = desktopSlideIndex >= 0 && desktopSlideIndex < desktopSlidesData.length - 1
        ? desktopSlidesData[desktopSlideIndex + 1]
        : null;

    const getSlideImageId = (slide) => {
        if (!slide) return null;
        if (slide.type === 'spread') {
            return slide.images[0]?.id;
        }
        return slide.id;
    };

    const imageData = imageId && exists ? _.result(content, ['files', imageId], null) : null;

    const grandparentName = useMemo(() => {
        if (!pageDataObj) return null;
        const parentId = getParentId(pageDataObj);
        if (!parentId) return null;
        const parentPage = pagesObj[parentId];
        if (!parentPage) return null;
        const grandparentId = getParentId(parentPage);
        if (grandparentId && pagesObj[grandparentId]) {
            return pagesObj[grandparentId].name;
        }
        return parentPage.name;
    }, [pageDataObj, pagesObj]);

    useEffect(() => {
        if (imageId) {
            trackImageOpened({
                imageName: imageId,
                imageDescription: imageData?.description,
                originalName: imageData?.originalname,
                pageName: name || 'Home',
                pageDescription: pageDataObj?.description,
                grandparentName,
                imageIndex: index >= 0 ? index + 1 : null,
                totalImages: flatImages.length,
            });
        }
    }, [imageId, name, imageData?.description, imageData?.originalname, pageDataObj?.description, grandparentName, index, flatImages.length]);

    const prevSlideData = desktopSlideIndex > 0 ? desktopSlidesData[desktopSlideIndex - 1] : null;
    const nextSlideData = desktopSlideIndex >= 0 && desktopSlideIndex < desktopSlidesData.length - 1
        ? desktopSlidesData[desktopSlideIndex + 1]
        : null;

    const isPhone = experience.phone;
    const isLandscape = experience.landscape;
    // page.slideshow.chrome: the corner (or hidden) for the logo, close button and counter.
    const chrome = useMemo(() => slideshowChromeFor(textSettings, { phone: isPhone }), [textSettings, isPhone]);

    // Phone-style editorial mode: small screens, and portrait big-tall
    // (the fallback from the desktop editorial layout).
    const usePhoneEditorialLayout = experience.phoneLayout;

    // Phone-style mode flattens spreads into single-image slides.
    // Other layouts preserve spread grouping.
    const touchSlidesData = useMemo(() => {
        if (usePhoneEditorialLayout) {
            return prepareEditorialSlidesData(rawImages, content?.files, page, pagesObj, hasText, textSlot);
        }

        const desktopLikeSlides = prepareDesktopEditorialSlidesData(rawImages, content?.files, page, pagesObj, hasText, textSlot);

        return desktopLikeSlides.map((slide) => {
            if (slide.type === 'text') return slide;
            if (slide.type === 'spread' && slide.images) return slide;
            if (slide.type === 'image' || (slide.file && slide.id)) {
                return { type: 'spread', images: [slide], url: slide.url };
            }
            return slide;
        });
    }, [usePhoneEditorialLayout, rawImages, content?.files, page, pagesObj, hasText, textSlot]);

    const [currentImageId, setCurrentImageId] = useState(imageId || null);
    // Opening on the page path starts on the text when it comes first; the
    // editor's Text view asks for the text wherever it sits.
    const [isOnTextSlide, setIsOnTextSlide] = useState(() => hasEditorialTextSlide
        && (startAtTextSlide || !imageId)
        && (textFirst || experience.view === 'text'));

    const prevImageIdRef = useRef(imageId);

    useEffect(() => {
        if (imageId && prevImageIdRef.current !== imageId) {
            setCurrentImageId(imageId);
            setIsOnTextSlide(false);
        }
        prevImageIdRef.current = imageId;
    }, [imageId]);

    const handleSlideChange = useCallback((swiper) => {
        const newIndex = swiper.activeIndex;
        if (newIndex < 0 || newIndex >= touchSlidesData.length) return;

        setMobileSlideIndex(newIndex);

        const newSlide = touchSlidesData[newIndex];
        if (newSlide) {
            if (newSlide.type === 'text') {
                setIsOnTextSlide(true);
            } else {
                const firstId = newSlide.type === 'spread'
                    ? newSlide.images?.[0]?.id
                    : newSlide.id;
                if (firstId) {
                    setCurrentImageId(firstId);
                    setIsOnTextSlide(false);
                }
            }

            if (newSlide.url && routerPathRef.current !== newSlide.url) {
                routerPathRef.current = newSlide.url;
                history.replace(newSlide.url);
            }
        }
    }, [touchSlidesData, history]);

    const getSlideIndexForImage = useCallback((imgId, slides) => {
        if (!imgId || !slides || slides.length === 0) return null;

        for (let i = 0; i < slides.length; i++) {
            const slide = slides[i];
            if (slide.type === 'text') continue;

            if (slide.type === 'spread') {
                if (slide.images?.some((img) => img.id === imgId)) {
                    return i;
                }
            } else if (slide.type === 'image') {
                if (slide.id === imgId) {
                    return i;
                }
            }
        }
        return null;
    }, []);

    const onTextSlide = hasEditorialTextSlide && isOnTextSlide;

    // The text slide can move under an open swiper (the editor changes where
    // it goes); keep showing it.
    const textSlideIndex = touchSlidesData.findIndex((slide) => slide.type === 'text');
    useEffect(() => {
        const swiper = swiperRef.current;
        if (!onTextSlide || textSlideIndex < 0 || !swiper || swiper.destroyed) return;
        if (swiper.activeIndex !== textSlideIndex) swiper.slideTo(textSlideIndex, 0);
    }, [onTextSlide, textSlideIndex]);
    const lastFlatImageId = flatImages[flatImages.length - 1] || null;
    // The text slide sits outside the image index: warm the image beside it.
    const prevFlatImageId = onTextSlide
        ? (textLast ? lastFlatImageId : null)
        : (index > 0 ? flatImages[index - 1] : null);
    const nextFlatImageId = onTextSlide
        ? (textFirst ? (flatImages[0] || null) : null)
        : (index >= 0 && index < flatImages.length - 1 ? flatImages[index + 1] : null);

    const prevFlatSlide = prevFlatImageId && files[prevFlatImageId]
        ? { id: prevFlatImageId, type: 'image', file: files[prevFlatImageId] }
        : null;
    const nextFlatSlide = nextFlatImageId && files[nextFlatImageId]
        ? { id: nextFlatImageId, type: 'image', file: files[nextFlatImageId] }
        : null;

    // Spreads are the normal adjacent unit on desktop editorial — preload both sides.
    const preloadableSlide = (slide) => (
        slide && slide.type !== 'text' && slide.type !== 'video' ? slide : null
    );
    const prev = usePhoneEditorialLayout ? prevFlatSlide : preloadableSlide(prevSlideData);
    const next = usePhoneEditorialLayout ? nextFlatSlide : preloadableSlide(nextSlideData);
    // Phone advances one picture at a time. Desktop advances one slide, which
    // may already be a two-page spread. The preload window uses that same unit.
    const preloadSlides = useMemo(() => (
        usePhoneEditorialLayout
            ? flatImages
                .map((id) => (files[id] ? { id, type: 'image', file: files[id] } : null))
                .filter(Boolean)
            : desktopSlidesData
    ), [usePhoneEditorialLayout, flatImages, files, desktopSlidesData]);
    const preloadCurrent = usePhoneEditorialLayout
        ? (!onTextSlide && imageId ? preloadSlides.find((slide) => slide.id === imageId) : null)
        : currentSlideData;

    const isDesktop = screenShape !== 'small' && !usePhoneEditorialLayout;

    const prevDesktopImageId = getSlideImageId(prevDesktopSlide);
    const nextDesktopImageId = getSlideImageId(nextDesktopSlide);

    const firstFlatImageId = flatImages[0] || null;
    const advanceTo = advanceUrl && !editorMode ? advanceUrl : null;
    let mobileUrlPrev;
    let mobileUrlNext;
    if (onTextSlide && textFirst) {
        mobileUrlPrev = closeUrl;
        mobileUrlNext = firstFlatImageId ? toUrl(page, firstFlatImageId, pagesObj) : null;
    } else if (onTextSlide) {
        mobileUrlPrev = lastFlatImageId ? toUrl(page, lastFlatImageId, pagesObj) : null;
        mobileUrlNext = advanceTo;
    } else {
        mobileUrlPrev = isFirst ? closeUrl : prevFlatImageId ? toUrl(page, prevFlatImageId, pagesObj) : null;
        mobileUrlNext = isLast && textLast
            ? toUrl(page, imageId, pagesObj)
            : (isLast && advanceTo ? advanceTo : nextFlatImageId ? toUrl(page, nextFlatImageId, pagesObj) : null);
    }
    const urlPrev = isDesktop
        ? (isFirstDesktopSlide ? closeUrl : prevDesktopImageId ? toUrl(page, prevDesktopImageId, pagesObj) : null)
        : mobileUrlPrev;
    const urlNext = isDesktop
        ? (isLastDesktopSlide && advanceUrl && !editorMode ? advanceUrl : nextDesktopImageId ? toUrl(page, nextDesktopImageId, pagesObj) : null)
        : mobileUrlNext;
    const navIsFirst = isDesktop ? isFirstDesktopSlide : (textFirst ? onTextSlide : isFirst && !onTextSlide);
    const navIsLast = isDesktop ? isLastDesktopSlide : (textLast ? onTextSlide : isLast);

    const isEditorialDesktop = !isPhone
        && !usePhoneEditorialLayout
        && (screenShape === 'big-wide' || screenShape === 'big-tall');

    const showOnboarding = useOnboarding(isTouchDevice, isEntered);
    useImagePreload(prev, next, preloadSlides, preloadCurrent);

    useSwipeToClose({
        enabled: isTouchDevice,
        onClose: () => closeWithFade(closeUrl, false, { dismissed: true }),
        figureRef: isEditorialDesktop ? editorialWrapperRef : figureRef,
        swipeWrapperRef: isEditorialDesktop ? editorialWrapperRef : swipeWrapperRef,
    });

    useSwipeNavigation({
        enabled: isTouchDevice && isEditorialDesktop,
        onPrev: () => {
            if (urlPrev && !navIsFirst) {
                history.push(urlPrev);
            }
        },
        onNext: () => {
            if (urlNext) {
                if (navIsLast && advanceUrl) {
                    closeWithFade(urlNext);
                } else {
                    history.push(urlNext);
                }
            } else if (navIsLast) {
                closeWithFade(closeUrl);
            }
        },
        targetRef: editorialWrapperRef,
        transformRef: imageContentRef,
        closeTransformRef: editorialWrapperRef,
        slideOut: true,
        canGoPrev: !navIsFirst,
        canGoNext: !navIsLast || !!advanceUrl,
    });

    useKeyboardNavigation({
        imageData,
        currentSlideData,
        spreadGap,
        urlPrev,
        urlNext,
        navIsFirst,
        navIsLast,
        isEditorial: true,
        advanceUrl: editorMode ? null : advanceUrl,
        closeUrl,
        navigateWithFade,
        closeWithFade,
    });

    useEffect(() => {
        if (imageContentRef.current) {
            imageContentRef.current.style.transition = '';
            imageContentRef.current.style.transform = '';
        }
    }, [desktopSlideIndex]);

    useEffect(() => {
        setIsHydrated(true);
        if (!ENABLE_VIEWER_PAGE_FADES) {
            setIsEntered(true);
            return undefined;
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setIsEntered(true);
            });
        });
        return undefined;
    }, []);

    useEffect(() => {
        if (!contentAppearDelayMs || contentAppearDelayMs <= 0) {
            setIsContentReady(true);
            return undefined;
        }
        setIsContentReady(false);
        const timeout = setTimeout(() => {
            setIsContentReady(true);
        }, contentAppearDelayMs);
        return () => clearTimeout(timeout);
    }, [contentAppearDelayMs]);

    if (!imageData) {
        return (
            <ImageNotFound
                imageId={imageId}
                index={index}
                page={page}
                images={images}
                matchParams={match?.params}
            />
        );
    }

    const siteTitle = shareSiteTitle(content, theme);
    const defaultAlt = imageData.description
        ? `${imageData.description}, ${siteTitle}`
        : `${name}, ${siteTitle}`;

    // The block this image came from may set its own caption and colours.
    const look = mediaLook(page ? pageDataObj : content?.home, imageId, previewLibrary(content), theme);
    // The page's own viewer colours beat the block's, which beat the theme's.
    const pageLook = pageViewerLook(page ? pageDataObj : content?.home, pagesObj);

    const containerStyle = {
        opacity: isEntered ? 1 : 0,
        transition: ENABLE_VIEWER_PAGE_FADES ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
        ...viewerLookVars(themeViewerLook(theme, chromeLayer)),
        ...look.vars,
        ...viewerLookVars(pageLook),
    };
    const lookFaces = look.fontFaceCss ? <style>{look.fontFaceCss}</style> : null;

    const { title: ogTitle, description: ogDescription } = resolveMediaShareCard(imageData, {
        content,
        theme,
        page: pageDataObj,
        pages: pagesObj,
        isHome: !page,
    });
    const helmetMeta = compactMeta([
        { property: 'og:title', content: ogTitle },
        { property: 'og:description', content: ogDescription },
        ...shareImageMeta(imageData, urlBase),
        { property: 'og:type', content: 'article' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: ogTitle },
        { name: 'twitter:description', content: ogDescription },
        { name: 'description', content: ogDescription },
    ]);

    const handlePrevClick = (e) => {
        e.preventDefault();
        if (usePhoneEditorialLayout && textFirst && !onTextSlide && isFirst) {
            setIsOnTextSlide(true);
            return;
        }
        if (usePhoneEditorialLayout && textLast && onTextSlide) {
            setIsOnTextSlide(false);
            return;
        }
        const slideForFade = usePhoneEditorialLayout
            ? { id: imageId, type: 'image', file: imageData }
            : currentSlideData;
        if (navIsFirst) {
            closeWithFade(urlPrev);
        } else {
            navigateWithFade(urlPrev, 'prev', imageData, slideForFade, spreadGap);
        }
    };

    const handleNextClick = (e) => {
        e.preventDefault();
        if (usePhoneEditorialLayout && textLast && !onTextSlide && isLast) {
            setIsOnTextSlide(true);
            return;
        }
        if (usePhoneEditorialLayout && textFirst && onTextSlide) {
            setIsOnTextSlide(false);
            if (firstFlatImageId) {
                setCurrentImageId(firstFlatImageId);
                const nextUrl = toUrl(page, firstFlatImageId, pagesObj);
                if (routerPathRef.current !== nextUrl) {
                    routerPathRef.current = nextUrl;
                    history.replace(nextUrl);
                }
            }
            return;
        }
        const slideForFade = usePhoneEditorialLayout
            ? { id: imageId, type: 'image', file: imageData }
            : currentSlideData;
        if (navIsLast && advanceUrl && !editorMode) {
            closeWithFade(urlNext);
        } else {
            navigateWithFade(urlNext, 'next', imageData, slideForFade, spreadGap);
        }
    };

    const handleCloseClick = (e) => {
        e.preventDefault();
        // In Appearance, X returns to the page root (not the parent site page).
        closeWithFade(closeUrl);
    };

    // Appearance preview: images on the current slide can be dragged and zoomed, so the
    // half-screen nav areas let the pointer through (arrows stay clickable) and a plain
    // click anywhere on the slide navigates by half instead.
    const placeInPreview = editorMode && !isTouchDevice;
    const onUpdateImage = placeInPreview
        ? (id, settings) => postEditorial({ action: 'order', images: updateRawImageSettings(rawImages, id, settings) })
        : undefined;
    const onResizeSheet = placeInPreview && !spreadFill ? (ratio, done) => {
        if (!done) {
            setDraftSpreadRatio(ratio);
            return;
        }
        if (ratio == null) {
            setDraftSpreadRatio(null);
            return;
        }
        const width = Math.round(ratio * 100) / 100;
        setDraftSpreadRatio(width);
        postEditorial({ action: 'spreadRatio', ratio: { width, height: 1 } });
    } : undefined;
    const handleSlideClick = placeInPreview ? (e) => {
        if (e.defaultPrevented) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const middle = screenShape === 'big-wide' ? window.innerWidth / 2 : rect.left + rect.width / 2;
        if (e.clientX < middle) {
            if (urlPrev && !navIsFirst) handlePrevClick(e);
        } else if (urlNext) {
            handleNextClick(e);
        }
    } : undefined;

    const filmstrip = editorMode ? (
        <EditorialFilmstrip
            images={rawImages}
            files={files}
            pagePath={page}
            pages={pagesObj}
            currentImageId={imageId}
            spreadGap={spreadGap}
            onSelect={(id, slideUrl) => {
                if (slideUrl) history.push(slideUrl);
                else if (id) history.push(toUrl(page, id, pagesObj));
            }}
        />
    ) : null;

    if (isEditorialDesktop) {
        const wrapperClass = screenShape === 'big-wide'
            ? 'fixed inset-0 z-[9999] bg-background flex flex-row overflow-hidden'
            : 'fixed inset-0 z-[9999] bg-background flex flex-col overflow-hidden';

        const figureClass = screenShape === 'big-wide'
            ? 'flex-1 relative bg-background flex flex-col overflow-hidden'
            : 'flex-[2] relative bg-background flex flex-col overflow-hidden';

        const spreadAspectRatioForLayout = pageDimensions
            ? pageDimensions.aspectRatio * (2 + (spreadGap || 0) / 100)
            : 4 / 3;

        return (
            <div ref={editorialWrapperRef} className={wrapperClass} style={containerStyle} data-viewer-root="">
                <ViewerLookContext.Provider value={pageLook}>
                <Helmet title={siteTitle} meta={helmetMeta} />
                {lookFaces}

                <EditorialLogo
                    siteTitle={siteTitle}
                    closeUrl={closeUrl}
                    onClose={closeWithFade}
                    context="slideshow"
                    corner={chrome.logo}
                />

                {/* big-wide: figure fills space, aside overlays on the left */}
                {screenShape === 'big-wide' && (
                    <div className="flex-1 relative min-w-0">
                        <figure
                            ref={figureRef}
                            className="absolute inset-0"
                            role="img"
                            aria-label={defaultAlt}
                            onClick={handleSlideClick}
                        >
                            <div ref={imageContentRef} className="absolute inset-0">
                                {isTouchDevice && prevSlideData && toSpreadData(prevSlideData) && (
                                    <div className="absolute inset-0" style={{ transform: 'translateX(-100%)' }}>
                                        <SpreadSlide
                                            spreadData={toSpreadData(prevSlideData)}
                                            alt={defaultAlt}
                                            gap={spreadGap}
                                            pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        />
                                    </div>
                                )}

                                {currentSlideData && toSpreadData(currentSlideData) && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(currentSlideData)}
                                        alt={defaultAlt}
                                        priority
                                        gap={spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        showDescription
                                        onUpdateImage={onUpdateImage}
                                        onResizeSheet={onResizeSheet}
                                    />
                                )}

                                {isTouchDevice && nextSlideData && toSpreadData(nextSlideData) && (
                                    <div className="absolute inset-0" style={{ transform: 'translateX(100%)' }}>
                                        <SpreadSlide
                                            spreadData={toSpreadData(nextSlideData)}
                                            alt={defaultAlt}
                                            gap={spreadGap}
                                            pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        />
                                    </div>
                                )}

                                {!isTouchDevice && isHydrated && prev && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(prev)}
                                        alt={defaultAlt}
                                        hidden
                                        zIndex={1}
                                        gap={spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                    />
                                )}
                                {!isTouchDevice && isHydrated && next && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(next)}
                                        alt={defaultAlt}
                                        hidden
                                        zIndex={1}
                                        gap={spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                    />
                                )}

                                {!isTouchDevice && fadeOut && toSpreadData(fadeOut.slideData || fadeOut.imageData) && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(fadeOut.slideData || fadeOut.imageData)}
                                        isOverlay
                                        opacity={fadeOpacity}
                                        zIndex={3}
                                        gap={fadeOut.spreadGap ?? spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        showDescription
                                    />
                                )}
                            </div>

                            {counterTotal > 0 && (
                                <ImageCounter
                                    current={counterCurrent}
                                    currentEnd={counterCurrentEnd}
                                    total={counterTotal}
                                    corner={chrome.counter}
                                />
                            )}
                        </figure>
                        {desktopText !== 'none' && (
                            <SlideshowText
                                placement={desktopText}
                                page={textPage}
                                pages={pagesForHtml}
                                files={files}
                                theme={theme}
                            />
                        )}
                        {/* Nav links overlay: left half = prev, right half = next; each spans exactly 50vw */}
                        <div className="fixed inset-0 z-20 pointer-events-none">
                            {!isTouchDevice && urlPrev && !navIsFirst && (
                                <div className={`absolute inset-y-0 left-0 w-[50vw] h-full ${placeInPreview ? '' : 'pointer-events-auto'}`} style={{ minWidth: '50vw' }}>
                                    <PrevLink url={urlPrev} onClick={handlePrevClick} fillContainer iconOnly={placeInPreview} />
                                </div>
                            )}
                            {!isTouchDevice && urlNext && (
                                <div className={`absolute inset-y-0 right-0 w-[50vw] h-full ${placeInPreview ? '' : 'pointer-events-auto'}`} style={{ minWidth: '50vw' }}>
                                    <NextLink url={urlNext} onClick={handleNextClick} fillContainer iconOnly={placeInPreview} />
                                </div>
                            )}
                        </div>
                        {/* Close button above nav overlay so X sits on top of right arrow */}
                        <div className="fixed inset-0 z-30 pointer-events-none">
                            <CloseButton url={closeUrl} onClick={handleCloseClick} corner={chrome.close} />
                        </div>
                    </div>
                )}

                {screenShape !== 'big-wide' && (
                <div className={`${figureClass} min-h-0 flex flex-col ${TALL_JUSTIFY[spreadAlign[0]] || 'justify-center'}`}>
                    {/* big-tall: spread area keeps its aspect ratio, at the top, middle or bottom per the align setting; aside/counter are pinned to the corners */}
                    <div
                        className={`${spreadFill ? 'flex-1 min-h-0' : 'flex-shrink-0 max-h-full'} w-full relative overflow-hidden`}
                        style={spreadFill ? undefined : { aspectRatio: spreadAspectRatioForLayout }}
                    >
                        <figure
                            ref={figureRef}
                            className="absolute inset-0"
                            role="img"
                            aria-label={defaultAlt}
                            onClick={handleSlideClick}
                        >
                            <div ref={imageContentRef} className="absolute inset-0">
                                {isTouchDevice && prevSlideData && toSpreadData(prevSlideData) && (
                                    <div className="absolute inset-0" style={{ transform: 'translateX(-100%)' }}>
                                        <SpreadSlide
                                            spreadData={toSpreadData(prevSlideData)}
                                            alt={defaultAlt}
                                            gap={spreadGap}
                                            pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                            fullWidthOnVertical
                                        />
                                    </div>
                                )}

                                {currentSlideData && toSpreadData(currentSlideData) && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(currentSlideData)}
                                        alt={defaultAlt}
                                        priority
                                        gap={spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        showDescription
                                        fullWidthOnVertical
                                        onUpdateImage={onUpdateImage}
                                        onResizeSheet={onResizeSheet}
                                    />
                                )}

                                {isTouchDevice && nextSlideData && toSpreadData(nextSlideData) && (
                                    <div className="absolute inset-0" style={{ transform: 'translateX(100%)' }}>
                                        <SpreadSlide
                                            spreadData={toSpreadData(nextSlideData)}
                                            alt={defaultAlt}
                                            gap={spreadGap}
                                            pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                            fullWidthOnVertical
                                        />
                                    </div>
                                )}

                                {!isTouchDevice && isHydrated && prev && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(prev)}
                                        alt={defaultAlt}
                                        hidden
                                        zIndex={1}
                                        gap={spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        fullWidthOnVertical
                                    />
                                )}
                                {!isTouchDevice && isHydrated && next && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(next)}
                                        alt={defaultAlt}
                                        hidden
                                        zIndex={1}
                                        gap={spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        fullWidthOnVertical
                                    />
                                )}

                                {!isTouchDevice && fadeOut && toSpreadData(fadeOut.slideData || fadeOut.imageData) && (
                                    <SpreadSlide
                                        spreadData={toSpreadData(fadeOut.slideData || fadeOut.imageData)}
                                        isOverlay
                                        opacity={fadeOpacity}
                                        zIndex={3}
                                        gap={fadeOut.spreadGap ?? spreadGap}
                                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                        showDescription
                                        fullWidthOnVertical
                                    />
                                )}
                            </div>

                            {!isTouchDevice && urlPrev && !navIsFirst && (
                                <PrevLink url={urlPrev} onClick={handlePrevClick} iconOnly={placeInPreview} />
                            )}
                            {!isTouchDevice && urlNext && (
                                <NextLink url={urlNext} onClick={handleNextClick} iconOnly={placeInPreview} />
                            )}
                        </figure>
                    </div>
                    {/* Close sits in the screen corner, not on the centred spread */}
                    <div className="fixed inset-0 z-30 pointer-events-none">
                        <CloseButton url={closeUrl} onClick={handleCloseClick} corner={chrome.close} />
                    </div>
                    {/* big-tall: description (aside) pinned to bottom-left corner of screen; counter at bottom-right */}
                    {screenShape === 'big-tall' && (
                        <div className="absolute inset-0 z-10 pointer-events-none">
                            {desktopText !== 'none' && (
                                <SlideshowText
                                    placement="corner"
                                    page={textPage}
                                    pages={pagesForHtml}
                                    files={files}
                                    theme={theme}
                                />
                            )}
                            {counterTotal > 0 && chrome.counter !== 'hidden' && (
                                <div className={`fixed ${chromeCornerClass('counter', chrome.counter)} pointer-events-auto z-10`}>
                                    <ImageCounter
                                        current={counterCurrent}
                                        currentEnd={counterCurrentEnd}
                                        total={counterTotal}
                                        inline
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                )}
            {filmstrip}
                </ViewerLookContext.Provider>
        </div>
        );
    }

    return (
        <figure
            ref={figureRef}
            className="fixed inset-0 w-full h-full z-[9999] bg-background flex flex-col"
            role="img"
            aria-label={defaultAlt}
            style={containerStyle}
            data-viewer-root=""
        >
            <ViewerLookContext.Provider value={pageLook}>
            <Helmet title={siteTitle} meta={helmetMeta} />
            {lookFaces}

            {!isHydrated && isContentReady && imageData && imageId && (
                usePhoneEditorialLayout ? (
                    <ImageSlide
                        imageData={{ id: imageId, type: 'image', file: imageData }}
                        alt={defaultAlt}
                        priority
                        isMobile
                        removePadding
                        description={imageData?.description}
                        imageId={imageId}
                        showDescription
                    />
                ) : toSpreadData({ id: imageId, file: imageData }) && (
                    <SpreadSlide
                        spreadData={toSpreadData({ id: imageId, file: imageData })}
                        alt={defaultAlt}
                        priority
                        gap={spreadGap}
                        pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                        showDescription
                        fullWidthOnVertical
                    />
                )
            )}

            {isHydrated && isContentReady && isTouchDevice && touchSlidesData.length > 0 && (
                <TouchSwiper
                    ref={swiperRef}
                    slidesData={touchSlidesData}
                    initialSlide={(() => {
                        if (onTextSlide) {
                            const textIndex = touchSlidesData.findIndex((slide) => slide.type === 'text');
                            if (textIndex >= 0) return textIndex;
                        }
                        if (currentImageId) {
                            const foundIndex = getSlideIndexForImage(currentImageId, touchSlidesData);
                            if (foundIndex !== null) {
                                return foundIndex;
                            }
                        }
                        return 0;
                    })()}
                    onSlideChange={handleSlideChange}
                    defaultAlt={defaultAlt}
                    swipeWrapperRef={swipeWrapperRef}
                    closeAnimationRef={figureRef}
                    pages={pagesForHtml}
                    files={files}
                    history={history}
                    pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                    spreadGap={spreadGap}
                    isLandscape={isLandscape}
                    siteTitle={siteTitle}
                    isEditorial={true}
                    onCloseOnLastSlide={() => closeWithFade(closeUrl)}
                    currentSlideIndex={mobileSlideIndex}
                    flatImages={flatImages}
                    textPage={textPage}
                    theme={theme}
                    counterCorner={chrome.counter}
                />
            )}

            {isHydrated && isPhone && chrome.logo !== 'hidden' && (
                <div
                    data-slideshow-logo=""
                    className={`fixed ${chromeCornerClass('phoneLogo', chrome.logo)} z-[10000] h-[22px] w-[100px]`}
                    style={{ opacity: 0.8 }}
                >
                    <SiteLogoMark logo={theme?.brand?.logo} context="slideshow" />
                </div>
            )}

            {isHydrated && isContentReady && !isTouchDevice && (
                <>
                    {usePhoneEditorialLayout ? (
                        <>
                            {onTextSlide ? (
                                <SlideshowText
                                    placement="slide"
                                    fill
                                    page={textPage}
                                    pages={pagesForHtml}
                                    files={files}
                                    theme={theme}
                                />
                            ) : (
                                <>
                                    {prev && (
                                        <ImageSlide
                                            imageData={prev}
                                            alt={defaultAlt}
                                            hidden
                                            zIndex={1}
                                            isMobile
                                            removePadding
                                        />
                                    )}
                                    <ImageSlide
                                        imageData={{ id: imageId, type: 'image', file: imageData }}
                                        alt={defaultAlt}
                                        priority
                                        isMobile
                                        removePadding
                                        description={imageData?.description}
                                        imageId={imageId}
                                        showDescription
                                    />
                                    {next && (
                                        <ImageSlide
                                            imageData={next}
                                            alt={defaultAlt}
                                            hidden
                                            zIndex={1}
                                            isMobile
                                            removePadding
                                        />
                                    )}
                                    {fadeOut && (
                                        <ImageSlide
                                            imageData={fadeOut.imageData}
                                            isOverlay
                                            opacity={fadeOpacity}
                                            zIndex={3}
                                            isMobile
                                            removePadding
                                            description={fadeOut.imageData?.description}
                                            showDescription
                                        />
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {prev && toSpreadData(prev) && (
                                <SpreadSlide
                                    spreadData={toSpreadData(prev)}
                                    alt={defaultAlt}
                                    hidden
                                    zIndex={1}
                                    gap={spreadGap}
                                    pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                    fullWidthOnVertical
                                />
                            )}

                            {currentSlideData && toSpreadData(currentSlideData) && (
                                <SpreadSlide
                                    spreadData={toSpreadData(currentSlideData)}
                                    alt={defaultAlt}
                                    priority
                                    gap={spreadGap}
                                    pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                    showDescription
                                    fullWidthOnVertical
                                />
                            )}

                            {next && toSpreadData(next) && (
                                <SpreadSlide
                                    spreadData={toSpreadData(next)}
                                    alt={defaultAlt}
                                    hidden
                                    zIndex={1}
                                    gap={spreadGap}
                                    pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                    fullWidthOnVertical
                                />
                            )}

                            {fadeOut && toSpreadData(fadeOut.slideData || fadeOut.imageData) && (
                                <SpreadSlide
                                    spreadData={toSpreadData(fadeOut.slideData || fadeOut.imageData)}
                                    isOverlay
                                    opacity={fadeOpacity}
                                    zIndex={3}
                                    gap={fadeOut.spreadGap ?? spreadGap}
                                    pageDimensions={pageDimensions} align={spreadAlign} fillScreen={spreadFill}
                                    showDescription
                                    fullWidthOnVertical
                                />
                            )}
                        </>
                    )}
                </>
            )}

            {isContentReady && <CloseButton url={closeUrl} onClick={handleCloseClick} corner={chrome.close} />}

            {isContentReady && urlPrev && !isTouchDevice && !navIsFirst && (
                <PrevLink url={urlPrev} onClick={handlePrevClick} />
            )}
            {isContentReady && urlNext && !isTouchDevice && (
                <NextLink url={urlNext} onClick={handleNextClick} />
            )}

            {isContentReady && !isTouchDevice && counterTotal > 0 && !(usePhoneEditorialLayout && onTextSlide) && (
                <ImageCounter
                    current={usePhoneEditorialLayout && index >= 0 ? index + 1 : counterCurrent}
                    currentEnd={usePhoneEditorialLayout ? null : counterCurrentEnd}
                    total={counterTotal}
                    corner={chrome.counter}
                />
            )}

            {isContentReady && showOnboarding && isTouchDevice && isHydrated && (
                <OnboardingOverlay />
            )}
            {filmstrip}
            </ViewerLookContext.Provider>
        </figure>
    );
}

SpreadViewer.propTypes = {
    match: PropTypes.object,
    history: PropTypes.object,
    location: PropTypes.object,
    startAtTextSlide: PropTypes.bool,
    overrideSlug: PropTypes.string,
    overrideImageId: PropTypes.string,
    contentAppearDelayMs: PropTypes.number,
    editorMode: PropTypes.bool,
};

export default SpreadViewer;
